// @ts-nocheck
import React from "react";
import { createRoot } from "react-dom/client";
import "jquery";
import "./index.scss";
import App from "./App";
import { Provider } from "react-redux";
import store from "./redux/store";
import logger from "./utils/logger";
import { debugLog } from "./utils/debugConfig";
import { InactivityProvider } from "./contexts/InactivityContext";
import { AIProvider } from "./components/ai";

// Initialize Extension listener early - this sets up the window message listener
import "./utils/Extension";

// Auto-reload on stale-chunk errors.
// When a new frontend build is deployed, any tab still running the old
// index.html has stale lazy-import paths (e.g. WelcomeComponent-CGxm6B-g.js).
// Navigating to a route that lazy-imports a now-missing chunk returns the
// SPA fallback index.html (text/html), and the browser refuses it with
// "Expected a JavaScript-or-Wasm module script". Force a single reload so
// the user picks up the new index.html with current chunk hashes.
//
// Guarded with sessionStorage to avoid reload loops if the error is
// persistent (e.g. actual 500 from server, or a bad deploy).
(function installStaleChunkReloader() {
  const FLAG = '__uiflow_reloaded_after_chunk_error__';
  const isChunkLoadError = (msg: string): boolean =>
    !!msg && (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('Expected a JavaScript-or-Wasm module script')
    );
  const tryReload = (source: string, msg: string) => {
    try {
      if (sessionStorage.getItem(FLAG) === '1') {
        // Already reloaded once; don't loop — show the error instead.
        console.error('[stale-chunk-reload] Already reloaded once; surfacing original error:', source, msg);
        return;
      }
      sessionStorage.setItem(FLAG, '1');
      console.warn('[stale-chunk-reload] Stale chunk detected from', source, '— reloading to fetch fresh index.html. Message:', msg);
      window.location.reload();
    } catch (e) {
      console.error('[stale-chunk-reload] Failed to reload:', e);
    }
  };
  // Clear the flag on a clean page load (not from bfcache).
  try {
    if (performance && (performance as any).getEntriesByType) {
      const nav: any = (performance as any).getEntriesByType('navigation')[0];
      if (nav && nav.type !== 'back_forward') {
        // Keep the flag for a few seconds after reload so we detect a bad
        // deploy (errors keep happening), then clear it on sustained success.
        setTimeout(() => { try { sessionStorage.removeItem(FLAG); } catch {} }, 10_000);
      }
    } else {
      setTimeout(() => { try { sessionStorage.removeItem(FLAG); } catch {} }, 10_000);
    }
  } catch {}

  window.addEventListener('error', (e) => {
    const msg = (e && (e.message || (e.error && e.error.message))) || '';
    if (isChunkLoadError(msg)) tryReload('window.error', msg);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r: any = e && e.reason;
    const msg = (r && (r.message || (typeof r === 'string' ? r : ''))) || '';
    if (isChunkLoadError(msg)) tryReload('unhandledrejection', msg);
  });
  // Vite-specific: preload errors have a dedicated event.
  window.addEventListener('vite:preloadError', (e: any) => {
    tryReload('vite:preloadError', e?.payload?.message || 'preload failed');
  });
})();

// Initialize logger
debugLog('🚨 INDEX.TSX EXECUTING - DIRECT CONSOLE LOG', window.location.href);
logger.info('APP', '=== Application Starting ===', {
  environment: process.env.NODE_ENV,
  timestamp: new Date().toISOString(),
  userAgent: navigator.userAgent
});

logger.info('APP', 'Extension.js imported - window message listener should be active');

// React 18 createRoot API
const container = document.getElementById("root");
debugLog('🚨 ROOT CONTAINER FOUND:', !!container);
const root = createRoot(container!);
debugLog('🚨 ABOUT TO RENDER APP COMPONENT');
root.render(
  <Provider store={store}>
    <InactivityProvider timeoutMinutes={45}>
      <AIProvider>
        <App />
      </AIProvider>
    </InactivityProvider>
  </Provider>
);

debugLog('🚨 APP COMPONENT RENDERED');
logger.info('APP', 'React application rendered');
